import type {
  GroupDataMessage,
  OnConnectedArgs,
  OnDisconnectedArgs,
  OnGroupDataMessageArgs,
  OnRejoinGroupFailedArgs,
  OnServerDataMessageArgs,
  OnStoppedArgs,
  ServerDataMessage,
} from '@azure/web-pubsub-client'

import { Notification, NotificationType } from '@/schema'

export enum NotificationStatus {
  Unread = 'unread',
  Read = 'read',
}

export enum NotificationFilterType {
  All = 'all',
  Assignments = 'assignments',
  Comments = 'comments',
  Deadlines = 'deadlines',
}

export interface NotificationDrawerState {
  isOpen: boolean
  activeTab: NotificationStatus
  isMuted: boolean
  isPinned: boolean
  filterType: NotificationFilterType
  notifications: Notification[]
  isLoading: boolean
  error: string | null
  hasMore: boolean
  currentOffset: number
  totalItems: number
}

export type NegotiateItem = {
  url: string
  base_url: string
  access_token: string
  expire_time: string
}

export type NegotiateMetadata = {
  correlation_id?: string
  request_id?: string
  timestamp?: string
  api_version?: string
  service_version?: string
  compliance_level?: string
  processing_time_ms?: number
}

export type NegotiateResponse = {
  success: boolean
  data: NegotiateItem[]
  metadata?: NegotiateMetadata
  warnings?: unknown
}

export enum ConnectionStatus {
  idle = 'idle',
  negotiating = 'negotiating',
  connecting = 'connecting',
  connected = 'connected',
  disconnected = 'disconnected',
  stopped = 'stopped',
  error = 'error',
}

export type PubSubMessage = {
  id: string
  receivedAt: string
  source: 'server' | 'group' | 'system'
  group?: string
  event?: string
  correlationId?: string
  data?: unknown
}

export enum GroupType {
  document = 'document',
  deliverable_set = 'deliverable_set',
  thread = 'thread',
  user = 'user',
  project = 'project',
  company = 'company',
  system = 'system',
  document_version = 'document_version',
}

export type JoinGroupItem = {
  group_type: GroupType
  group_id: string
}

export type JoinGroupsRequest = {
  group: JoinGroupItem[]
  user_id: string
}

export type LeaveGroupsRequest = {
  group_names: string[]
  user_id: string
}

export interface NotificationMetadata {
  deliverable_set_id?: string
  document_id?: string
  file_name?: string
  filename?: string
  document_name?: string
  comment_id?: string
  root_comment_id?: string
  document_version_id?: string
  thread_id?: string
  [key: string]: string | undefined
}

export type NotificationData = {
  data: {
    title: string
    message: string
    notification_id?: string
    metadata?: NotificationMetadata
    notification_type?: NotificationType
  }
}

export enum NotificationSignalType {
  Connected = 'connected',
  Disconnected = 'disconnected',
  Stopped = 'stopped',
  GroupMessage = 'group-message',
  ServerMessage = 'server-message',
  RejoinGroupFailed = 'rejoin-group-failed',
}

export type NotificationSignal =
  | {
      type: NotificationSignalType.Connected
      id: string
      timestamp: string
      connectionId?: string
      userId?: string
      rawEvent: OnConnectedArgs
    }
  | {
      type: NotificationSignalType.Disconnected
      id: string
      timestamp: string
      rawEvent: OnDisconnectedArgs
    }
  | {
      type: NotificationSignalType.Stopped
      id: string
      timestamp: string
      rawEvent: OnStoppedArgs
    }
  | {
      type: NotificationSignalType.GroupMessage
      id: string
      timestamp: string
      group: string
      dataType: GroupDataMessage['dataType']
      data: GroupDataMessage['data']
      fromUserId: GroupDataMessage['fromUserId']
      sequenceId?: GroupDataMessage['sequenceId']
      rawEvent: OnGroupDataMessageArgs
    }
  | {
      type: NotificationSignalType.ServerMessage
      id: string
      timestamp: string
      dataType: ServerDataMessage['dataType']
      data: NotificationData
      sequenceId?: ServerDataMessage['sequenceId']
      rawEvent: OnServerDataMessageArgs
    }
  | {
      type: NotificationSignalType.RejoinGroupFailed
      id: string
      timestamp: string
      group?: string
      rawEvent: OnRejoinGroupFailedArgs
    }

export type EventCallback = (data: NotificationSignal) => void
